import * as Sentry from '@sentry/browser';
import type {AgentPage, AgentRegistration} from './agentDeclaration';
import {setDeployedAgentIds} from './agentVisibility';
import {isRecord, validateAgentRegistration} from './validateAgentDeclaration';

const UNKNOWN_AGENT_ID = 'unknown';

// Real agents are added here; the next agent needs only its declaration, its runner and one line.
const PRODUCTION_AGENTS: AgentRegistration[] = [];

const registrations: AgentRegistration[] = [];

let hasRegisteredKnownAgents = false;

const readAgentId = (candidate: unknown): string => {
	const declaration = isRecord(candidate) && isRecord(candidate.declaration) ? candidate.declaration : undefined;
	const agentId = declaration ? declaration.id : undefined;
	return typeof agentId === 'string' && agentId.trim().length > 0 ? agentId : UNKNOWN_AGENT_ID;
};

// Refusing must never throw: registration runs when the toolbar module loads, so a throw would break the page.
const refuse = (message: string): false => {
	// eslint-disable-next-line no-console
	console.warn(message);
	Sentry.captureMessage(message, 'warning');
	return false;
};

export const registerAgent = (candidate: AgentRegistration): boolean => {
	const agentId = readAgentId(candidate);
	const slotGaps = validateAgentRegistration(candidate);
	if (slotGaps.length > 0) {
		return refuse(`Agent declaration "${agentId}" refused. Missing or invalid slots: ${slotGaps.join(', ')}`);
	}
	if (registrations.some(registration => registration.declaration.id === agentId)) {
		return refuse(`Agent declaration "${agentId}" refused. An agent with this id is already registered.`);
	}
	registrations.push(candidate);
	return true;
};

export const ensureAgentsRegistered = (): void => {
	if (hasRegisteredKnownAgents) {
		return;
	}
	hasRegisteredKnownAgents = true;
	PRODUCTION_AGENTS.forEach(registerAgent);
	// The stub agents are required inside this literal NODE_ENV check, not imported, so production
	// builds drop the branch and never bundle them.
	if (process.env.NODE_ENV === 'development') {
		// eslint-disable-next-line @typescript-eslint/no-var-requires
		const {STUB_AGENTS}: {STUB_AGENTS: AgentRegistration[]} = require('./testing/stubAgents');
		STUB_AGENTS.forEach(registerAgent);
		setDeployedAgentIds(STUB_AGENTS.map(registration => registration.declaration.id));
	}
};

export const getAgentsForPage = (page: AgentPage): AgentRegistration[] =>
	registrations.filter(registration => registration.declaration.pages.includes(page));

export const clearAgentRegistry = (): void => {
	registrations.length = 0;
	hasRegisteredKnownAgents = false;
	setDeployedAgentIds([]);
};
